import React, { useMemo, useCallback, ComponentType } from "react";
import { FetchResult } from "@apollo/client";
import {
    Settings, useGetWorkflowByIdQuery, useGetWorkspaceEventsQuery, WorkflowTriggerType, useGetFormQuery, useGetFormDataQuery,
    useGetExternalFormFieldsQuery,
    Form,
    Workflow,
    FormField,
    ActionContextType,
    WorkflowTriggerInput,
    useDetachWorkflowMutation,
    useAttachWorkflowMutation,
    useSaveSettingsMutation,
    useSaveFormMutation,
    FormType,
    WorkspaceEvent,
    useSaveWorkspaceEventsMutation,
    GetFormDocument,
    useSaveChatSettingsMutation,
    useGetChatSettingsLazyQuery,
    SaveChatSettingsMutation,
    SaveSettingsMutation,
} from "../../../../__generated__/api";
import { AutomationTab } from "../../../../components/workflow-v3/providers/WorkflowModalProvider";
import { removeTypename, removeTypenameList } from "../../../../api";
import WORKFLOW_TRIGGER_TYPES from "./WorkflowTriggerTypes";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import useGeneralSettingsProperties, {
    ActionProperties,
    ChannelTagProperties,
    EmailProperties, EmojiProperties, InboundWebhookProperties, PropertiesByTrigger, WorkspaceActionProperties,
} from "./general-settings/useGeneralSettingsProperties";
import AutomationFlow from "../../../../components/workflow-v3/AutomationFlow";
import BaseGeneralSettings from "./general-settings/BaseGeneralDetails";
import usePortal from "../../../../contexts/PortalContext";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { ReplacementGroup } from "../../../../components/replacement-group";

type GeneralDetailsPanel = ComponentType<{
    properties: PropertiesByTrigger<WorkflowTriggerType>,
    formData: Form,
    workspaceEvents: WorkspaceEvent[],
}>;

const diffTriggers = (
    existingIds: string[],
    newTriggers: WorkflowTriggerInput[],
) => {
    const toDetach = existingIds.exclude(newTriggers.toIdList(trigger => trigger.referenceId));
    const toAttach = newTriggers.exclude(existingIds, trigger => (typeof trigger === "string" ? trigger : trigger.referenceId));
    return { toDetach, toAttach };
};

export default function ViewAutomation({
    settings,
    workflowId,
    form,
    onHide,
    startTab,
    contextType,
    input,
    hideGeneralDetailsTab,
}: {
    settings?: Settings;
    workflowId?: string;
    form?: { formId: string | undefined; formType: WorkflowTriggerType };
    onHide: () => void;
    startTab?: AutomationTab;
    contextType?: ActionContextType;
    input?: ReplacementGroup[];
    hideGeneralDetailsTab?: boolean;
}) {
    const { data: workspaceEvents } = useGetWorkspaceEventsQuery();
    const { id: portalId } = usePortal();
    const [saveForm] = useSaveFormMutation({
        refetchQueries: [
            {
                query: GetFormDocument,
                variables: {
                    id: form?.formId, hasId: true,
                },
            },
        ],
    });

    const [saveWorkspaceEvents] = useSaveWorkspaceEventsMutation();

    const { data: workflow } = useGetWorkflowByIdQuery({
        skip: !workflowId,
        variables: {
            id: workflowId,
        },
    });

    const { data: formData } = useGetFormQuery({
        skip: !form?.formId,
        variables: {
            id: form?.formId,
            hasId: true,
        },
    });

    const externalFormId = useMemo(() => formData?.form?.externalFormId, [formData?.form?.externalFormId]);

    const { data: formDataQuery } = useGetFormDataQuery({
        variables: {
            shouldIncludeTemplateFields: false,
        },
    });

    const { data: externalData } = useGetExternalFormFieldsQuery({
        variables: {
            id: externalFormId,
        },
        skip: externalFormId == null,
    });

    const [attachWorkflow] = useAttachWorkflowMutation();
    const [detachWorkflow] = useDetachWorkflowMutation();
    const [saveSettings] = useSaveSettingsMutation();
    const [saveChatSettings] = useSaveChatSettingsMutation();
    const [getChatSettings] = useGetChatSettingsLazyQuery();

    const triggerIds = useMemo(
        () => workflow?.workflow?.triggers?.map(trigger => trigger.referenceId) ?? [],
        [workflow?.workflow?.triggers],
    );

    const fields = useMemo(() => (structuredClone(formData?.form?.fields) ?? [])
        .map(field => removeTypename(field)) ?? [], [formData?.form?.fields]);

    const fieldMap = useMemo(() => formDataQuery?.formFields.toIdMap("id") ?? {}, [formDataQuery?.formFields]);
    const formFields = useMemo(() => (externalData?.externalFormFields as FormField[]
        ?? fields.mapFilter(row => (row.fieldId ? fieldMap[row.fieldId] : undefined))), [externalData, fields, fieldMap]);

    const actionsContextType: ActionContextType | undefined = useMemo(
        () => contextType ?? WORKFLOW_TRIGGER_TYPES[form?.formType as WorkflowTriggerType]?.actionContextType,
        [form?.formType, contextType],
    );

    const workflowTitle = useMemo(() => {
        const type = form?.formType as WorkflowTriggerType;
        return `${WORKFLOW_TRIGGER_TYPES[type].text}: ${workflow?.workflow?.name}`;
    }, [workflow]);

    const GeneralDetailsPanel = useMemo(
        () => {
            if (hideGeneralDetailsTab) {
                return null;
            }
            if (form?.formType) {
                return WORKFLOW_TRIGGER_TYPES[form?.formType]?.component as GeneralDetailsPanel;
            }

            return BaseGeneralSettings as GeneralDetailsPanel;
        },
        [form?.formType, hideGeneralDetailsTab],
    );

    const { properties: formDesignerProperties } = useFormBuilder(
        builder => builder
            .array("fields", () => formData?.form?.fields ?? [])
            .property("externalFormId", () => formData?.form?.externalFormId),

        [formData],
    );

    const { properties: generalSettingsProperties } = useGeneralSettingsProperties({
        triggerType: form?.formType as WorkflowTriggerType,
        formData: formData?.form as Form,
        settings,
        workspaceEvents: workspaceEvents?.workspaceEvents ?? [],
        workflow: workflow?.workflow as Workflow,
    });

    const triggerUpdater = useCallback(async (triggers: WorkflowTriggerInput[]) => {
        const { toDetach, toAttach } = diffTriggers(triggerIds, triggers);

        if (toDetach.isNotEmpty) {
            await detachWorkflow({
                variables: {
                    id: workflow?.workflow?.id,
                    referenceIds: toDetach,
                },
            });
        }

        if (toAttach.isNotEmpty) {
            await attachWorkflow({
                variables: {
                    id: workflow?.workflow?.id,
                    triggers: toAttach,
                },
            });
        }
    }, [triggerIds, workflow?.workflow?.id, detachWorkflow, attachWorkflow]);

    const handleSaveForm = useCallback(async () => {
        const triggerType = form?.formType as WorkflowTriggerType;

        if (triggerType === WorkflowTriggerType.InboundWebhook) {
            await saveForm({
                variables: {
                    id: formData?.form?.id,
                    form: {
                        type: FormType.InboundWebhook,
                        workflowId: workflow?.workflow?.id,
                        fields: removeTypenameList(formData?.form?.fields ?? []),
                        name: formData?.form?.name ?? "",
                        userGroupIds: (generalSettingsProperties as InboundWebhookProperties).groups.value,
                        description: formData?.form?.description ?? "",
                    },
                },
            });
            return;
        }

        if (triggerType === WorkflowTriggerType.Action) {
            await saveForm({
                variables: {
                    id: formData?.form?.id,
                    form: {
                        workflowId: workflow?.workflow?.id,
                        fields: formDesignerProperties?.fields?.value ?? formData?.form?.fields ?? [],
                        externalFormId: formDesignerProperties?.externalFormId?.value,
                        type: formData?.form?.type,
                        name: generalSettingsProperties.name.value,
                        description: generalSettingsProperties.description.value,
                        isCreatePrivate: formData?.form?.isCreatePrivate,
                        userGroupIds: (generalSettingsProperties as ActionProperties).groups.value,
                        tags: formData?.form?.tags,
                        iconUrl: formData?.form?.iconUrl,
                        shouldCreateOnOnboarding: formData?.form?.shouldCreateOnOnboarding,
                        jobFunctions: formData?.form?.jobFunctions,
                        deflection: formData?.form?.deflection?.isEnabled ? {
                            isEnabled: formData?.form?.deflection?.isEnabled,
                            identity: formData?.form?.deflection?.identity,
                        } : undefined,
                        actionSettings: {
                            startCondition: (generalSettingsProperties as ActionProperties).startCondition.value,
                        },
                    },
                },
            });
            return;
        }

        if (triggerType === WorkflowTriggerType.Email) {
            const emails = (generalSettingsProperties as EmailProperties).emails.value;

            const triggers = emails.map(email => ({
                referenceId: email,
                text: email,
                type: WorkflowTriggerType.Email,
            }));

            await triggerUpdater(triggers);
            return;
        }

        if (triggerType === WorkflowTriggerType.ChannelTag) {
            const channelTags = (generalSettingsProperties as ChannelTagProperties).channelTags.value;

            const triggers = channelTags.map(tag => ({
                referenceId: `urn:fq:channel-tag:${tag.value}`,
                text: tag.label,
                type: WorkflowTriggerType.ChannelTag,
            }));

            await triggerUpdater(triggers);
            return;
        }

        if (triggerType === WorkflowTriggerType.WebChat) {
            await attachWorkflow({
                variables: {
                    id: workflow?.workflow?.id,
                    triggers: [{
                        referenceId: portalId,
                        text: WorkflowTriggerType.WebChat,
                        type: WorkflowTriggerType.WebChat,
                    }],
                },
            });
            return;
        }

        if (triggerType === WorkflowTriggerType.Emoji) {
            const emojiActions = removeTypenameList(settings?.channelSlack?.emojiActions ?? []);
            const clonedEmojiActions = [...emojiActions];
            const executeGroups = (generalSettingsProperties as EmojiProperties).groups.value;
            const newEmoji = (generalSettingsProperties as EmojiProperties).emoji.value;

            if (!newEmoji) {
                // don't save if the emoji is empty
                return;
            }

            clonedEmojiActions.push({
                emoji: newEmoji,
                allowedExecuteGroups: executeGroups,
                description: "Emoji",
                workflowId: workflow?.workflow?.id,
            });

            const selectedSources = (generalSettingsProperties as EmojiProperties).conversationSources.value ?? [];
            const existingSources = ((workflow?.workflow?.triggers ?? [])
                .mapFilter(trigger => trigger.referenceId, trigger => trigger.type === WorkflowTriggerType.Emoji));

            const wasGlobal = existingSources.includes(portalId);
            const isGlobal = selectedSources.isEmpty;

            const tobeRemovedSources = existingSources.exclude(selectedSources.map(source => source.value));

            const savePromises: Promise<FetchResult<SaveChatSettingsMutation | SaveSettingsMutation>>[] = [];

            if (isGlobal) {
                // Save to global settings
                const newEmojiActions = removeTypenameList(settings?.channelSlack?.emojiActions ?? [])
                    .concat({
                        allowedExecuteGroups: executeGroups,
                        emoji: newEmoji,
                        workflowId: workflow?.workflow?.id,
                    })
                    .dedupe(action => action.emoji);

                savePromises.push(saveSettings({
                    variables: {
                        channelSlack: {
                            emojiActions: newEmojiActions,
                        },
                    },
                }));

                // Remove from any previously selected sources
                const removalPromisesGlobal = existingSources.map(async sourceId => {
                    const chatSettings = await getChatSettings({ variables: { id: sourceId } });
                    const existing = removeTypenameList(
                        chatSettings.data?.chatSettings?.options?.slack?.emojiActions ?? [],
                    );
                    const filtered = existing.filter(
                        action => !(
                            action.emoji === newEmoji
                            && action.workflowId === workflow?.workflow?.id
                        ),
                    );
                    return saveChatSettings({
                        variables: {
                            id: sourceId.value,
                            settings: { options: { slack: { emojiActions: filtered } } },
                        },
                    });
                });

                savePromises.push(...removalPromisesGlobal);
            } else {
                // Save to each selected source
                const addPromises = selectedSources.map(async source => {
                    const chatSettings = await getChatSettings({ variables: { id: source.value } });
                    const newEmojiActions = removeTypenameList(
                        chatSettings.data?.chatSettings?.options?.slack?.emojiActions ?? [],
                    )
                        .concat({
                            allowedExecuteGroups: executeGroups,
                            emoji: newEmoji,
                            workflowId: workflow?.workflow?.id,
                        })
                        .dedupe(action => action.emoji);

                    return saveChatSettings({
                        variables: {
                            id: source.value,
                            settings: { options: { slack: { emojiActions: newEmojiActions } } },
                        },
                    });
                });
                savePromises.push(...addPromises);

                // If previously global, remove from global settings
                if (wasGlobal) {
                    const filtered = removeTypenameList(settings?.channelSlack?.emojiActions ?? [])
                        .filter(action => !(
                            action.emoji === newEmoji
                            && action.workflowId === workflow?.workflow?.id
                        ));

                    savePromises.push(saveSettings({
                        variables: {
                            channelSlack: {
                                emojiActions: filtered,
                            },
                        },
                    }));
                }

                // Also remove from any previously selected sources that are now deselected
                const removalPromises = tobeRemovedSources.map(async sourceId => {
                    const chatSettings = await getChatSettings({ variables: { id: sourceId } });
                    const existing = removeTypenameList(
                        chatSettings.data?.chatSettings?.options?.slack?.emojiActions ?? [],
                    );
                    const filtered = existing.filter(
                        action => !(
                            action.emoji === newEmoji
                            && action.workflowId === workflow?.workflow?.id
                        ),
                    );
                    return saveChatSettings({
                        variables: {
                            id: sourceId,
                            settings: { options: { slack: { emojiActions: filtered } } },
                        },
                    });
                });
                savePromises.push(...removalPromises);
            }

            const triggers = (
                isGlobal
                    ? [{
                        referenceId: portalId,
                        text: "Global",
                        type: WorkflowTriggerType.Emoji,
                    }]
                    : selectedSources.map(source => ({
                        referenceId: source.value,
                        text: source.label,
                        type: WorkflowTriggerType.Emoji,
                    })));

            await LoggingHandler.instance.all(
                ...savePromises,
                triggerUpdater(triggers),
            );
            return;
        }

        if (triggerType === WorkflowTriggerType.WorkspaceAction) {
            const eventTypeId = (generalSettingsProperties as WorkspaceActionProperties).event.value;
            const currentWorkflowId = workflow?.workflow?.id;

            if (workspaceEvents && eventTypeId && currentWorkflowId) {
                const eventsWithoutCurrentWorkflow = workspaceEvents
                    .workspaceEvents?.filter(event => event.workflowId !== currentWorkflowId) ?? [];

                const newEvent: WorkspaceEvent = {
                    eventTypeId,
                    workflowId: currentWorkflowId,
                };

                await triggerUpdater([{
                    referenceId: eventTypeId,
                    text: eventTypeId,
                    type: WorkflowTriggerType.WorkspaceAction,
                }]);

                await saveWorkspaceEvents({
                    variables: {
                        events: [...removeTypenameList(eventsWithoutCurrentWorkflow), newEvent],
                    },
                });
            }
        }
    }, [generalSettingsProperties, triggerUpdater, workflow, settings]);

    const generalDetailsPanel = useMemo(() => (
        GeneralDetailsPanel ? (
            <GeneralDetailsPanel
                properties={generalSettingsProperties}
                formData={formData?.form as Form}
                workspaceEvents={workspaceEvents?.workspaceEvents ?? [] as WorkspaceEvent[]}
            />
        ) : null
    ), [GeneralDetailsPanel, generalSettingsProperties, formData?.form, workspaceEvents?.workspaceEvents]);

    if (!workflow?.workflow) {
        return null;
    }

    return (
        <AutomationFlow
            onWorkflowInit={handleSaveForm}
            generalDetailsPanel={generalDetailsPanel}
            title={workflowTitle as string}
            workflow={workflow.workflow as Workflow}
            onHide={onHide}
            formFields={formFields}
            actionsContextType={actionsContextType}
            form={formData?.form as Form}
            formDesignerProperties={formDesignerProperties}
            generalSettingsProperties={generalSettingsProperties}
            startTab={startTab}
            input={input}
        />
    );
}

ViewAutomation.defaultProps = {
    settings: undefined,
    workflowId: undefined,
    form: undefined,
    startTab: undefined,
    contextType: undefined,
    input: undefined,
    hideGeneralDetailsTab: false,
};
